import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  DeleteRounded,
} from '@mui/icons-material';
import type { LandingPillar, LandingPillarIcon } from '@dosfilos/domain';
import {
  MAX_PILLARS,
  MAX_PILLAR_TEXT,
  PILLAR_ICONS,
  moveItem,
  newPillar,
  validatePillar,
  type PillarError,
} from '../../../lib/landing-content';
import { PILLAR_ICON } from '../../public/pillar-icons';

/**
 * Editor de los pilares del landing (Configuración → Landing). Los escribe el
 * seminario; sin pilares, la sección no aparece.
 */
export function PillarsEditor({
  pillars,
  onChange,
}: {
  pillars: LandingPillar[];
  onChange: (next: LandingPillar[]) => void;
}) {
  const { t } = useTranslation('sis');
  const patch = (id: string, change: Partial<LandingPillar>) =>
    onChange(pillars.map((p) => (p.id === id ? ({ ...p, ...change } as LandingPillar) : p)));
  const incomplete = pillars.some((p) => validatePillar(p).length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        display="block"
        mb={1}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {t('settings.landing.pillars.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('settings.landing.pillars.description')}
      </Typography>

      <Stack spacing={1.5}>
        {pillars.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('settings.landing.pillars.empty')}
          </Typography>
        )}
        {incomplete && <Alert severity="warning">{t('settings.landing.pillars.incomplete')}</Alert>}

        {pillars.map((p, idx) => {
          const errors = validatePillar(p);
          const errorText = (f: PillarError) =>
            errors.includes(f) ? t(`settings.landing.pillars.errors.${f}`) : undefined;
          return (
            <Paper key={p.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'flex-start' }}
                >
                  <TextField
                    select
                    label={t('settings.landing.pillars.iconLabel')}
                    value={p.icon}
                    onChange={(e) => patch(p.id, { icon: e.target.value as LandingPillarIcon })}
                    size="small"
                    sx={{ minWidth: 170 }}
                  >
                    {PILLAR_ICONS.map((key) => {
                      const Icon = PILLAR_ICON[key];
                      return (
                        <MenuItem key={key} value={key}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Icon fontSize="small" />
                            <span>{t(`settings.landing.pillars.icons.${key}`)}</span>
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  <TextField
                    label={t('settings.landing.pillars.titleLabel')}
                    placeholder={t('settings.landing.pillars.titlePlaceholder')}
                    value={p.title}
                    onChange={(e) => patch(p.id, { title: e.target.value })}
                    size="small"
                    fullWidth
                    required
                    error={!!errorText('title')}
                    helperText={errorText('title')}
                  />
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title={t('settings.landing.pillars.moveUp')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('settings.landing.pillars.moveUp')}
                          disabled={idx === 0}
                          onClick={() => onChange(moveItem(pillars, idx, -1))}
                        >
                          <ArrowUpwardRounded fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('settings.landing.pillars.moveDown')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('settings.landing.pillars.moveDown')}
                          disabled={idx === pillars.length - 1}
                          onClick={() => onChange(moveItem(pillars, idx, 1))}
                        >
                          <ArrowDownwardRounded fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('settings.landing.pillars.remove')}>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('settings.landing.pillars.remove')}
                        onClick={() => onChange(pillars.filter((x) => x.id !== p.id))}
                      >
                        <DeleteRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                <TextField
                  label={t('settings.landing.pillars.textLabel')}
                  value={p.text}
                  onChange={(e) => patch(p.id, { text: e.target.value.slice(0, MAX_PILLAR_TEXT) })}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  required
                  error={!!errorText('text')}
                  helperText={
                    errorText('text') ??
                    t('settings.landing.pillars.textHelper', {
                      count: p.text.length,
                      max: MAX_PILLAR_TEXT,
                    })
                  }
                />
              </Stack>
            </Paper>
          );
        })}

        <Box>
          {pillars.length < MAX_PILLARS ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRounded />}
              onClick={() => onChange([...pillars, newPillar(crypto.randomUUID())])}
            >
              {t('settings.landing.pillars.add')}
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {t('settings.landing.pillars.limit', { max: MAX_PILLARS })}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
